import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { trasfertaApi, configApi, tecniciApi } from '../services/api'
import AutocompleteInput from '../components/AutocompleteInput'
import { useAuth } from '../contexts/AuthContext'
import { Car, Plus, ChevronLeft, ChevronRight, CheckCircle2, Clock, AlertTriangle, FileText, Download, Eye, X, Trash2, Pencil, Check, PauseCircle, PlayCircle } from 'lucide-react'
import toast from 'react-hot-toast'

// ─── Tipi ─────────────────────────────────────────────────────────────────────

interface Trasferta {
  id: number
  uuid: string
  progetto_cantiere: string
  nr_commessa: string
  project_manager: string
  destinazione: string
  motivo_trasferta: string
  data_partenza: string
  data_rientro: string
  giorni_totali: number
  operatore_1: string
  operatore_2?: string
  operatore_3?: string
  operatore_4?: string
  operatore_1_tecnico_id?: number | null
  operatore_2_tecnico_id?: number | null
  operatore_3_tecnico_id?: number | null
  operatore_4_tecnico_id?: number | null
  num_operatori: number
  mezzo_trasporto?: string
  targa?: string
  assegnatario_responsabile?: string
  telepass_presente: boolean
  carta_carburante_presente: boolean
  attrezzatura_necessaria_presente: boolean
  pernottamento_necessario: boolean
  nome_albergo?: string
  ragione_sociale_albergo?: string
  iban_albergo?: string
  importo_albergo?: number
  email_albergo?: string
  telefono_albergo?: string
  nome_riferimento_albergo?: string
  via_albergo?: string
  citta_albergo?: string
  trasferta_forfettaria: boolean
  importo_forfettario?: number
  nord_italia: boolean
  mezza_pensione: boolean
  pranzi_da_togliere?: number
  cene_da_togliere?: number
  importo_giornaliero_operatore: number
  totale_operatori?: number
  totale_detrazione_pasti?: number
  totale_trasferta?: number
  nome_bonifico_dipendenti?: string
  note?: string
  richiedente_id?: number
  richiedente_email: string
  stato: string
  created_at?: string
  bonifico_albergo_confermato_at?: string
  bonifico_dipendenti_confermato_at?: string
  bonifico_albergo_confermato_da?: string
  bonifico_dipendenti_confermato_da?: string
  escalation_count: number
  ultima_escalation_at?: string
  distinta_albergo_path?: string
  distinta_dipendenti_path?: string
  escalation_sospensioni?: {
    dipendenti?: SospensioneEscalation
    albergo?: SospensioneEscalation
  }
  bonifici_dipendenti?: BonificoDipendente[]
}

interface BonificoDipendente {
  id: number
  tecnico_id: number
  ordine: number
  importo: number
  iban_snapshot?: string
  banca_snapshot?: string
  intestatario_snapshot?: string
  confermato_at?: string
  confermato_da?: string
  distinta_path?: string
}

interface SospensioneEscalation {
  fino_al?: string
  indefinita: boolean
  sospesa_da: string
  sospesa_at: string
  nota?: string
}

interface FatturaCortesiaStato {
  id: number
  uuid: string
  progetto_cantiere: string
  nr_commessa: string
  richiedente_email: string
  bonifico_albergo_confermato_at?: string
  fattura_cortesia_scadenza?: string
  giorni_rimanenti?: number
  stato: 'countdown' | 'scaduta' | 'sospesa' | 'bypassata'
  fattura_caricata?: boolean
}

const FATTURA_CORTESIA_ADMIN_EMAIL = 'stefano.sborchia@imeco.me'

// ─── Utility ──────────────────────────────────────────────────────────────────

const MEZZO_OPTIONS = ['Auto aziendale', 'Auto propria', 'Furgone aziendale', 'Treno', 'Aereo', 'Altro']

function calcola_diaria(nord: boolean, mp: boolean, cfg?: Record<string, number>): number {
  const c = cfg || {}
  if (nord) {
    if (mp) return (c.diaria_nord_mezza_pranzo ?? 20) + (c.diaria_nord_mezza_cena ?? 0)
    return (c.diaria_nord_intera_pranzo ?? 20) + (c.diaria_nord_intera_cena ?? 20)
  }
  if (mp) return (c.diaria_sud_mezza_pranzo ?? 10) + (c.diaria_sud_mezza_cena ?? 0)
  return (c.diaria_sud_intera_pranzo ?? 15) + (c.diaria_sud_intera_cena ?? 15)
}

function fmt_euro(v?: number | null): string {
  if (v == null) return '—'
  return `€ ${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmt_data(d?: string): string {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString('it-IT') } catch { return d }
}

function fmt_data_ora(d?: string): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return d }
}

function sospensioneAttiva(s?: SospensioneEscalation): boolean {
  if (!s) return false
  if (s.indefinita) return true
  if (!s.fino_al) return false
  return new Date(s.fino_al) > new Date()
}

function valida_iban(_v: string): boolean {
  return true
}

function BadgeStato({ stato, bonifico_dip, bonifico_alb, pernottamento, sospesa_dip, sospesa_alb }: {
  stato: string; bonifico_dip?: string; bonifico_alb?: string; pernottamento: boolean
  sospesa_dip?: boolean; sospesa_alb?: boolean
}) {
  if (stato === 'bozza') return (
    <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
      <FileText size={11} /> Bozza
    </span>
  )
  const tuttiOk = bonifico_dip && (!pernottamento || bonifico_alb)
  const parziale = (bonifico_dip || bonifico_alb) && !tuttiOk
  const dipPendente = !bonifico_dip
  const albPendente = pernottamento && !bonifico_alb
  const escalationSospesa = (dipPendente && sospesa_dip) || (albPendente && sospesa_alb)
  if (tuttiOk || stato === 'completata') return (
    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
      <CheckCircle2 size={11} /> Completata
    </span>
  )
  if (parziale) return (
    <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
      <Clock size={11} /> Parziale
      {escalationSospesa && <span title="Escalation sospesa"><PauseCircle size={11} /></span>}
    </span>
  )
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
      <AlertTriangle size={11} /> Bonifici pendenti
      {escalationSospesa && <span title="Escalation sospesa"><PauseCircle size={11} /></span>}
    </span>
  )
}

function FatturaCortesiaBanner() {
  const qc = useQueryClient()
  const [uploadingId, setUploadingId] = useState<number | null>(null)
  const fileInputRef = useRef<Record<number, HTMLInputElement | null>>({})

  const { data: mie = [] } = useQuery({
    queryKey: ['fattura-cortesia-mie'],
    queryFn: () => trasfertaApi.fatturaCortesiaMie().then(r => r.data as FatturaCortesiaStato[]),
    refetchOnWindowFocus: true,
  })

  const uploadMut = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => trasfertaApi.uploadFatturaCortesia(id, file),
    onSuccess: () => {
      toast.success('Fattura di cortesia caricata')
      qc.invalidateQueries({ queryKey: ['fattura-cortesia-mie'] })
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      setUploadingId(null)
    },
    onError: () => { toast.error('Errore nel caricamento'); setUploadingId(null) },
  })

  if (mie.length === 0) return null

  return (
    <div className="space-y-2">
      {mie.map(m => {
        const stile = m.stato === 'scaduta'
          ? 'bg-red-50 border-red-300 text-red-800'
          : m.stato === 'sospesa'
          ? 'bg-slate-50 border-slate-300 text-slate-700'
          : 'bg-amber-50 border-amber-300 text-amber-800'
        const messaggio = m.stato === 'scaduta'
          ? `Non puoi richiedere nuove trasferte con bonifico albergo finché non carichi la fattura di cortesia per la trasferta #${m.id} (${m.progetto_cantiere}).`
          : m.stato === 'sospesa'
          ? `Termine sospeso da Stefano per la trasferta #${m.id} (${m.progetto_cantiere}) — nessuna azione richiesta per ora.`
          : `Carica la fattura di cortesia per la trasferta #${m.id} (${m.progetto_cantiere}) entro ${m.giorni_rimanenti} giorni.`
        return (
          <div key={m.id} className={`border rounded-lg px-4 py-3 flex items-center justify-between gap-3 ${stile}`}>
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} />
              <span className="text-sm font-medium">{messaggio}</span>
            </div>
            {m.stato !== 'sospesa' && (
              <>
                <input
                  type="file" accept="application/pdf,image/*" className="hidden"
                  ref={el => { fileInputRef.current[m.id] = el }}
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (file) { setUploadingId(m.id); uploadMut.mutate({ id: m.id, file }) }
                  }}
                />
                <button
                  onClick={() => fileInputRef.current[m.id]?.click()}
                  disabled={uploadingId === m.id}
                  className="btn-secondary text-xs whitespace-nowrap"
                >
                  {uploadingId === m.id ? 'Caricamento...' : 'Carica fattura'}
                </button>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

function PannelloFatturaCortesia() {
  const qc = useQueryClient()
  const [posticipaId, setPosticipaId] = useState<number | null>(null)
  const [nuovaData, setNuovaData] = useState('')

  const { data: stato = [] } = useQuery({
    queryKey: ['fattura-cortesia-stato'],
    queryFn: () => trasfertaApi.fatturaCortesiaStato().then(r => r.data as FatturaCortesiaStato[]),
  })

  const invalidaTutto = () => {
    qc.invalidateQueries({ queryKey: ['fattura-cortesia-stato'] })
    qc.invalidateQueries({ queryKey: ['fattura-cortesia-mie'] })
  }

  const posticipaMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: string }) => trasfertaApi.posticipaFatturaCortesia(id, data),
    onSuccess: () => { toast.success('Scadenza posticipata'); invalidaTutto(); setPosticipaId(null) },
    onError: () => toast.error('Errore nel posticipo'),
  })
  const sospendiMut = useMutation({
    mutationFn: (id: number) => trasfertaApi.sospendiFatturaCortesia(id, { indefinita: true }),
    onSuccess: () => { toast.success('Termine sospeso'); invalidaTutto() },
    onError: () => toast.error('Errore nella sospensione'),
  })
  const riprendiMut = useMutation({
    mutationFn: (id: number) => trasfertaApi.riprendiFatturaCortesia(id),
    onSuccess: () => { toast.success('Sospensione rimossa'); invalidaTutto() },
    onError: () => toast.error('Errore'),
  })
  const [uploadingId, setUploadingId] = useState<number | null>(null)
  const fileInputRef = useRef<Record<number, HTMLInputElement | null>>({})
  const uploadMut = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => trasfertaApi.uploadFatturaCortesia(id, file),
    onSuccess: () => { toast.success('Fattura di cortesia caricata'); invalidaTutto(); setUploadingId(null) },
    onError: () => { toast.error('Errore nel caricamento'); setUploadingId(null) },
  })
  const sbloccaMut = useMutation({
    mutationFn: (id: number) => trasfertaApi.sbloccaFatturaCortesia(id),
    onSuccess: () => { toast.success('Trasferta sbloccata'); invalidaTutto() },
    onError: () => toast.error('Errore nello sblocco'),
  })

  if (stato.length === 0) {
    return <div className="card p-6 text-center text-slate-400 text-sm">Nessuna trasferta con bonifico albergo in attesa di fattura di cortesia.</div>
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <th className="text-left px-3 py-2 text-xs text-slate-500">Trasferta</th>
            <th className="text-left px-3 py-2 text-xs text-slate-500">Operatore</th>
            <th className="text-left px-3 py-2 text-xs text-slate-500">Scadenza</th>
            <th className="text-left px-3 py-2 text-xs text-slate-500">Giorni</th>
            <th className="text-left px-3 py-2 text-xs text-slate-500">Stato</th>
            <th className="text-center px-3 py-2 text-xs text-slate-500">Azioni</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-50">
            {stato.map(s => (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-xs text-slate-700">#{s.id} — {s.progetto_cantiere}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{s.richiedente_email}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmt_data_ora(s.fattura_cortesia_scadenza)}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{s.giorni_rimanenti ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className={`badge ${s.stato === 'scaduta' ? 'bg-red-100 text-red-700' : s.stato === 'sospesa' ? 'bg-slate-100 text-slate-600' : s.stato === 'bypassata' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {s.stato}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-center gap-1.5 flex-wrap">
                    {posticipaId === s.id ? (
                      <>
                        <input type="date" className="input text-xs py-1 w-32" value={nuovaData} onChange={e => setNuovaData(e.target.value)} />
                        <button className="btn-primary text-xs py-1" onClick={() => nuovaData && posticipaMut.mutate({ id: s.id, data: nuovaData })}>OK</button>
                        <button className="btn-ghost text-xs py-1" onClick={() => setPosticipaId(null)}>Annulla</button>
                      </>
                    ) : (
                      <button className="btn-ghost text-xs py-1" onClick={() => { setPosticipaId(s.id); setNuovaData('') }}>Posticipa</button>
                    )}
                    {s.stato === 'sospesa' ? (
                      <button className="btn-ghost text-xs py-1" onClick={() => riprendiMut.mutate(s.id)}><PlayCircle size={12} className="inline mr-1" />Riprendi</button>
                    ) : (
                      <button className="btn-ghost text-xs py-1" onClick={() => sospendiMut.mutate(s.id)}><PauseCircle size={12} className="inline mr-1" />Sospendi</button>
                    )}
                    <button className="btn-ghost text-xs py-1 text-emerald-700" onClick={() => sbloccaMut.mutate(s.id)}>Sblocca</button>
                    {!s.fattura_caricata && s.stato !== 'bypassata' && (
                      <>
                        <input
                          type="file" accept="application/pdf,image/*" className="hidden"
                          ref={el => { fileInputRef.current[s.id] = el }}
                          onChange={e => {
                            const file = e.target.files?.[0]
                            if (file) { setUploadingId(s.id); uploadMut.mutate({ id: s.id, file }) }
                            e.target.value = ''
                          }}
                        />
                        <button className="btn-ghost text-xs py-1 text-blue-700" disabled={uploadingId === s.id}
                          onClick={() => fileInputRef.current[s.id]?.click()}>
                          {uploadingId === s.id ? 'Caricamento...' : 'Carica fattura'}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── STEPPER NUOVA TRASFERTA ──────────────────────────────────────────────────

const STEPS = ['Informazioni', 'Date e Personale', 'Costi', 'Riepilogo']

const emptyForm = {
  progetto_cantiere: '', nr_commessa: '', project_manager: '', destinazione: '', motivo_trasferta: '',
  data_partenza: '', data_rientro: '',
  operatore_1: '', operatore_2: '', operatore_3: '', operatore_4: '',
  operatore_1_tecnico_id: null as number | null, operatore_2_tecnico_id: null as number | null,
  operatore_3_tecnico_id: null as number | null, operatore_4_tecnico_id: null as number | null,
  mezzo_trasporto: '', targa: '', assegnatario_responsabile: '',
  telepass_presente: false, carta_carburante_presente: false, attrezzatura_necessaria_presente: false,
  pernottamento_necessario: false,
  nome_albergo: '', ragione_sociale_albergo: '', iban_albergo: '', importo_albergo: '',
  email_albergo: '', telefono_albergo: '', nome_riferimento_albergo: '', via_albergo: '', citta_albergo: '',
  trasferta_forfettaria: false, importo_forfettario: '',
  nord_italia: false, mezza_pensione: false,
  pranzi_da_togliere: 0, cene_da_togliere: 1,
  nome_bonifico_dipendenti: '', note: '',
}

function mapTrasfertaToForm(t: Trasferta) {
  return {
    progetto_cantiere: t.progetto_cantiere, nr_commessa: t.nr_commessa,
    project_manager: t.project_manager, destinazione: t.destinazione,
    motivo_trasferta: t.motivo_trasferta,
    data_partenza: t.data_partenza, data_rientro: t.data_rientro,
    operatore_1: t.operatore_1, operatore_2: t.operatore_2 ?? '',
    operatore_3: t.operatore_3 ?? '', operatore_4: t.operatore_4 ?? '',
    operatore_1_tecnico_id: t.operatore_1_tecnico_id ?? null, operatore_2_tecnico_id: t.operatore_2_tecnico_id ?? null,
    operatore_3_tecnico_id: t.operatore_3_tecnico_id ?? null, operatore_4_tecnico_id: t.operatore_4_tecnico_id ?? null,
    mezzo_trasporto: t.mezzo_trasporto ?? '', targa: t.targa ?? '',
    assegnatario_responsabile: t.assegnatario_responsabile ?? '',
    telepass_presente: t.telepass_presente, carta_carburante_presente: t.carta_carburante_presente,
    attrezzatura_necessaria_presente: t.attrezzatura_necessaria_presente,
    pernottamento_necessario: t.pernottamento_necessario,
    nome_albergo: t.nome_albergo ?? '', ragione_sociale_albergo: t.ragione_sociale_albergo ?? '',
    iban_albergo: t.iban_albergo ?? '', importo_albergo: t.importo_albergo != null ? String(t.importo_albergo) : '',
    email_albergo: t.email_albergo ?? '', telefono_albergo: t.telefono_albergo ?? '',
    nome_riferimento_albergo: t.nome_riferimento_albergo ?? '', via_albergo: t.via_albergo ?? '',
    citta_albergo: t.citta_albergo ?? '',
    trasferta_forfettaria: t.trasferta_forfettaria, importo_forfettario: t.importo_forfettario != null ? String(t.importo_forfettario) : '',
    nord_italia: t.nord_italia, mezza_pensione: t.mezza_pensione,
    pranzi_da_togliere: t.pranzi_da_togliere ?? 0, cene_da_togliere: t.cene_da_togliere ?? 1,
    nome_bonifico_dipendenti: t.nome_bonifico_dipendenti ?? '', note: t.note ?? '',
  }
}

// Dropdown commesse con ricerca testuale (filtra per codice o ragione sociale mentre si scrive)
function CommessaCombobox({ commesseList, value, onChange, onSelectCommessa }: {
  commesseList: { id: number; codice: string; ragione_sociale: string; label: string }[]
  value: string
  onChange: (v: string) => void
  onSelectCommessa: (c: { codice: string; ragione_sociale: string }) => void
}) {
  const [query, setQuery] = useState(value)
  const [aperto, setAperto] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setQuery(value) }, [value])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setAperto(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const filtrate = query.trim()
    ? commesseList.filter(c => c.label.toLowerCase().includes(query.trim().toLowerCase()))
    : commesseList

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className="input"
        style={{ width: '100%' }}
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setAperto(true) }}
        onFocus={() => setAperto(true)}
        placeholder="Seleziona o scrivi per cercare per codice/ragione sociale..."
        autoComplete="off"
      />
      {aperto && filtrate.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', margin: 0, padding: 0,
          listStyle: 'none', maxHeight: 220, overflowY: 'auto',
        }}>
          {filtrate.map(c => (
            <li
              key={c.id}
              onMouseDown={() => { onSelectCommessa(c); setQuery(c.codice); setAperto(false) }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #F1F5F9' }}
            >
              <span style={{ fontWeight: 600 }}>{c.codice}</span>
              <span style={{ color: '#94A3B8', fontSize: 11, marginLeft: 8 }}>{c.ragione_sociale}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function NuovaTrasfertaModal({ onClose, trasfertaId, initialData, onSaved }: {
  onClose: () => void
  trasfertaId?: number
  initialData?: Trasferta
  onSaved?: (updated: Trasferta & { conferme_invalidate?: string[] }) => void
}) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const isEdit = trasfertaId != null
  const [step, setStep] = useState(0)
  const [form, setForm] = useState(initialData ? mapTrasfertaToForm(initialData) : emptyForm)
  const [extraOp, setExtraOp] = useState(() => {
    if (!initialData) return 0
    return [initialData.operatore_2, initialData.operatore_3, initialData.operatore_4].filter(Boolean).length
  })

  const f = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }))

  // Tariffe diarie (include pranzo/cena per scenario)
  const { data: diarieCfg } = useQuery({
    queryKey: ['diarie-cfg'],
    queryFn: () => configApi.getDiarie().then((r: any) => r.data),
    staleTime: 10 * 60 * 1000,
  })
  const _cfg = (diarieCfg ?? {}) as Record<string, number>
  const _isMezza = form.mezza_pensione && !form.trasferta_forfettaria
  const importo_pranzo: number = form.nord_italia
    ? (_cfg[ _isMezza ? 'diaria_nord_mezza_pranzo' : 'diaria_nord_intera_pranzo'] ?? (_isMezza ? 20 : 20))
    : (_cfg[ _isMezza ? 'diaria_sud_mezza_pranzo'  : 'diaria_sud_intera_pranzo']  ?? (_isMezza ? 10 : 15))
  const importo_cena: number = form.nord_italia
    ? (_cfg[ _isMezza ? 'diaria_nord_mezza_cena'   : 'diaria_nord_intera_cena']   ?? (_isMezza ?  0 : 20))
    : (_cfg[ _isMezza ? 'diaria_sud_mezza_cena'    : 'diaria_sud_intera_cena']    ?? (_isMezza ?  0 : 15))

  // Query tecnici attivi per il picker operatori (con IBAN in anagrafica)
  const { data: tecniciList = [] } = useQuery({
    queryKey: ['tecnici-attivi-trasferte'],
    queryFn: () => tecniciApi.list({ per_page: 100, is_active: true }).then((r: any) => r.data.items),
    staleTime: 60_000,
  })

  // Query commesse per il menu a tendina nr_commessa
  const { data: commesseList = [] } = useQuery<{ id: number; codice: string; ragione_sociale: string; label: string }[]>({
    queryKey: ['commesse-trasferta'],
    queryFn: () => configApi.getCommesse().then((r: any) => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const pmPrecompilato = user
    ? `${(user as any).nome ?? ''} ${(user as any).cognome ?? ''}`.trim()
    : ''

  const operatoriCompilati = [form.operatore_1, ...[form.operatore_2, form.operatore_3, form.operatore_4].slice(0, extraOp)].filter(Boolean) as string[]
  const soloUnOp = operatoriCompilati.length === 1

  // Precompila project manager con nome+cognome utente loggato (solo in creazione)
  useEffect(() => {
    if (!isEdit && pmPrecompilato) f('project_manager', pmPrecompilato)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Mezza pensione: cene incluse nel pernottamento → 0; altrimenti default 1
  const skipCeneInit = useRef(isEdit)
  useEffect(() => {
    if (skipCeneInit.current) { skipCeneInit.current = false; return }
    setForm(p => ({ ...p, cene_da_togliere: form.mezza_pensione ? 0 : 1 }))
  }, [form.mezza_pensione]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill assegnatario e beneficiario quando c'è un solo operatore
  const skipSoloOpInit = useRef(isEdit)
  useEffect(() => {
    if (skipSoloOpInit.current) { skipSoloOpInit.current = false; return }
    if (soloUnOp && form.operatore_1) {
      setForm(p => ({ ...p, assegnatario_responsabile: form.operatore_1, nome_bonifico_dipendenti: form.operatore_1 }))
    }
  }, [soloUnOp, form.operatore_1]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill targa dal tecnico quando mezzo = Auto aziendale
  const skipTargaInit = useRef(isEdit)
  useEffect(() => {
    if (skipTargaInit.current) { skipTargaInit.current = false; return }
    if (form.mezzo_trasporto !== 'Auto aziendale' || !form.operatore_1) return
    trasfertaApi.getTecnicoTarga(form.operatore_1).then((r: any) => {
      if (r.data?.targa) f('targa', r.data.targa)
    }).catch(() => {})
  }, [form.mezzo_trasporto, form.operatore_1]) // eslint-disable-line react-hooks/exhaustive-deps

  const giorni = useMemo(() => {
    if (!form.data_partenza || !form.data_rientro) return 0
    const d = (new Date(form.data_rientro).getTime() - new Date(form.data_partenza).getTime()) / 86400000
    return d >= 0 ? d + 1 : 0
  }, [form.data_partenza, form.data_rientro])

  const numOp = 1 + [form.operatore_2, form.operatore_3, form.operatore_4].slice(0, extraOp).filter(Boolean).length
  const notti = Math.max(0, giorni - 1)
  const diaria = form.trasferta_forfettaria
    ? calcola_diaria(form.nord_italia, false, _cfg as any)
    : calcola_diaria(form.nord_italia, form.mezza_pensione, _cfg as any)
  const tot_op = diaria * numOp * giorni
  const importo_forf_parsed = form.trasferta_forfettaria && form.importo_forfettario
    ? parseFloat(form.importo_forfettario as string) || 0
    : 0
  const tot_alb = form.trasferta_forfettaria
    ? importo_forf_parsed * notti          // struttura: notti = giorni - 1
    : (form.pernottamento_necessario && form.importo_albergo ? parseFloat(form.importo_albergo) : 0)
  const detrazione_pranzi = (form.pranzi_da_togliere || 0) * importo_pranzo * numOp
  const detrazione_cene = (form.cene_da_togliere || 0) * importo_cena * numOp
  const detrazione_pasti = detrazione_pranzi + detrazione_cene
  const tot = tot_op + tot_alb - detrazione_pasti

  const ibanOk = !form.iban_albergo || valida_iban(form.iban_albergo)

  const canNext = useMemo(() => {
    if (step === 0) return !!(form.progetto_cantiere && form.nr_commessa && form.project_manager && form.destinazione && form.motivo_trasferta)
    if (step === 1) return !!(form.data_partenza && form.data_rientro && form.operatore_1 && form.assegnatario_responsabile && giorni > 0)
    if (step === 2) {
      if (!form.nome_bonifico_dipendenti) return false
      if (form.trasferta_forfettaria) return !!(form.importo_forfettario && parseFloat(form.importo_forfettario as string) > 0)
      return ibanOk
    }
    return true
  }, [step, form, giorni, ibanOk])

  const _buildPayload = () => {
    const payload: any = {
      ...form,
      importo_albergo: form.importo_albergo ? parseFloat(form.importo_albergo as string) : null,
      operatore_2: extraOp >= 1 ? form.operatore_2 || null : null,
      operatore_3: extraOp >= 2 ? form.operatore_3 || null : null,
      operatore_4: extraOp >= 3 ? form.operatore_4 || null : null,
      operatore_1_tecnico_id: form.operatore_1_tecnico_id || null,
      operatore_2_tecnico_id: extraOp >= 1 ? form.operatore_2_tecnico_id || null : null,
      operatore_3_tecnico_id: extraOp >= 2 ? form.operatore_3_tecnico_id || null : null,
      operatore_4_tecnico_id: extraOp >= 3 ? form.operatore_4_tecnico_id || null : null,
    }
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null })
    return payload
  }

  const invioMut = useMutation({
    mutationFn: () => trasfertaApi.crea(_buildPayload()),
    onSuccess: () => {
      toast.success('Trasferta inviata! Email di notifica inviata.')
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      onClose()
    },
    onError: (e: any) => {
      const d = e?.response?.data?.detail
      toast.error(Array.isArray(d) ? d.map((x: any) => x.msg).join(', ') : (d || 'Errore invio'))
    },
  })

  const bozzaMut = useMutation({
    mutationFn: () => trasfertaApi.creaBozza(_buildPayload()),
    onSuccess: () => {
      toast.success('Bozza salvata.')
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      onClose()
    },
    onError: (e: any) => {
      const d = e?.response?.data?.detail
      toast.error(Array.isArray(d) ? d.map((x: any) => x.msg).join(', ') : (d || 'Errore salvataggio'))
    },
  })

  const updateMut = useMutation({
    mutationFn: () => trasfertaApi.update(trasfertaId as number, _buildPayload()),
    onSuccess: (r) => {
      toast.success('Trasferta aggiornata.')
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      const invalidate: string[] = r.data.conferme_invalidate ?? []
      invalidate.forEach((parte: string) =>
        toast(`⚠️ Conferma bonifico ${parte} annullata — nuova richiesta inviata`, { icon: '⚠️' })
      )
      onSaved?.(r.data)
      onClose()
    },
    onError: (e: any) => {
      const d = e?.response?.data?.detail
      toast.error(Array.isArray(d) ? d.map((x: any) => x.msg).join(', ') : (d || 'Errore aggiornamento'))
    },
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="card w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Car size={18} className="text-blue-600" />
            <h2 className="font-semibold text-slate-800">{isEdit ? `Modifica trasferta #${trasfertaId}` : 'Nuova Richiesta di Trasferta'}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>

        {/* Stepper */}
        <div className="flex border-b border-slate-100">
          {STEPS.map((s, i) => (
            <div key={i} className={`flex-1 text-center py-3 text-xs font-medium border-b-2 transition-colors
              ${i === step ? 'border-blue-600 text-blue-700' : i < step ? 'border-green-500 text-green-600' : 'border-transparent text-slate-400'}`}>
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs mr-1.5
                ${i === step ? 'bg-blue-600 text-white' : i < step ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {i < step ? '✓' : i + 1}
              </span>
              {s}
            </div>
          ))}
        </div>

        <div className="p-6 space-y-4">

          {/* STEP 0 — Informazioni Generali */}
          {step === 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">N° Commessa *</label>
                  {commesseList.length > 0 ? (
                    <CommessaCombobox
                      commesseList={commesseList}
                      value={form.nr_commessa}
                      onChange={v => f('nr_commessa', v)}
                      onSelectCommessa={c => setForm(p => ({ ...p, nr_commessa: c.codice, progetto_cantiere: c.ragione_sociale }))}
                    />
                  ) : (
                    <AutocompleteInput campo="nr_commessa" value={form.nr_commessa} onChange={v => f('nr_commessa', v)} placeholder="es. C2024-0042" />
                  )}
                </div>
                <div className="col-span-2">
                  <label className="label">Progetto / Cantiere *</label>
                  <AutocompleteInput campo="progetto_cantiere" value={form.progetto_cantiere} onChange={v => f('progetto_cantiere', v)} placeholder="es. Ampliamento Rete Fibra — Lazio Sud" />
                </div>
                <div>
                  <label className="label">Project Manager</label>
                  <input className="input bg-slate-100 text-slate-500 cursor-not-allowed" value={form.project_manager}
                    readOnly
                    placeholder={pmPrecompilato || 'Nome e cognome'}
                  />
                  <p className="text-xs text-slate-400 mt-1">Compilato dal tuo profilo — non modificabile</p>
                </div>
                <div className="col-span-2">
                  <label className="label">Destinazione *</label>
                  <AutocompleteInput campo="destinazione" value={form.destinazione} onChange={v => f('destinazione', v)} placeholder="es. Frosinone (FR)" />
                </div>
                <div className="col-span-2">
                  <label className="label">Motivo della trasferta *</label>
                  <textarea className="input" rows={3} value={form.motivo_trasferta} onChange={e => f('motivo_trasferta', e.target.value)} placeholder="Descrivi brevemente lo scopo dell'intervento..." />
                </div>
              </div>
            </>
          )}

          {/* STEP 1 — Date e Personale */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Data partenza *</label>
                  <input type="date" className="input" value={form.data_partenza} onChange={e => f('data_partenza', e.target.value)} />
                </div>
                <div>
                  <label className="label">Data rientro *</label>
                  <input type="date" className="input" min={form.data_partenza} value={form.data_rientro} onChange={e => f('data_rientro', e.target.value)} />
                </div>
              </div>
              {giorni > 0 && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                  <span className="text-2xl font-bold text-blue-700">{giorni}</span>
                  <span className="text-sm text-blue-600">giorno{giorni !== 1 ? 'i' : ''} di trasferta</span>
                </div>
              )}

              <div className="space-y-2">
                <label className="label">Operatori *</label>
                {[
                  { key: 'operatore_1', label: 'Operatore 1 *', required: true },
                  ...(extraOp >= 1 ? [{ key: 'operatore_2', label: 'Operatore 2', required: false }] : []),
                  ...(extraOp >= 2 ? [{ key: 'operatore_3', label: 'Operatore 3', required: false }] : []),
                  ...(extraOp >= 3 ? [{ key: 'operatore_4', label: 'Operatore 4', required: false }] : []),
                ].map(({ key, label, required }) => {
                  const tecnicoIdKey = `${key}_tecnico_id`
                  const selectedTecnicoId = (form as any)[tecnicoIdKey] as number | null
                  const selectedTecnico = (tecniciList as any[]).find((t: any) => t.id === selectedTecnicoId)
                  return (
                    <div key={key}>
                      <div className="flex gap-2 items-center">
                        <select className="select flex-1" value={selectedTecnicoId || ''}
                          onChange={e => {
                            const tecnicoId = e.target.value ? Number(e.target.value) : null
                            const tecnico = (tecniciList as any[]).find((t: any) => t.id === tecnicoId)
                            const nomeCompleto = tecnico ? `${tecnico.nome} ${tecnico.cognome}` : ''
                            setForm(p => ({ ...p, [key]: nomeCompleto, [tecnicoIdKey]: tecnicoId }))
                          }}
                          required={required}>
                          <option value="">— {label} —</option>
                          {(tecniciList as any[]).map((t: any) => (
                            <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
                          ))}
                        </select>
                        {key !== 'operatore_1' && (
                          <button type="button" onClick={() => { setForm(p => ({ ...p, [key]: '', [tecnicoIdKey]: null })); setExtraOp(p => p - 1) }}
                            className="text-slate-400 hover:text-red-500"><X size={15} /></button>
                        )}
                      </div>
                      {selectedTecnicoId && !selectedTecnico?.iban && (
                        <p className="text-xs mt-1" style={{ color: 'var(--mac-orange)' }}>
                          ⚠️ IBAN mancante in anagrafica — il bonifico resterà in attesa finché non viene inserito
                        </p>
                      )}
                    </div>
                  )
                })}
                {extraOp < 3 && (
                  <button type="button" onClick={() => setExtraOp(p => p + 1)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium">+ Aggiungi operatore</button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Mezzo di trasporto</label>
                  <select className="select" value={form.mezzo_trasporto} onChange={e => f('mezzo_trasporto', e.target.value)}>
                    <option value="">— Seleziona —</option>
                    {MEZZO_OPTIONS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Targa</label>
                  <input className="input" value={form.targa} onChange={e => f('targa', e.target.value.toUpperCase())} placeholder="es. AB123CD" />
                </div>
                <div className="col-span-2">
                  <label className="label">Assegnatario responsabile *</label>
                  {soloUnOp ? (
                    <input className="input bg-slate-100 text-slate-500 cursor-not-allowed"
                      value={form.assegnatario_responsabile} readOnly />
                  ) : (
                    <select className="select" value={form.assegnatario_responsabile}
                      onChange={e => f('assegnatario_responsabile', e.target.value)}>
                      <option value="">— Seleziona —</option>
                      {operatoriCompilati.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                {([
                  { key: 'telepass_presente', label: 'Telepass' },
                  { key: 'carta_carburante_presente', label: 'Carta carburante' },
                  { key: 'attrezzatura_necessaria_presente', label: 'Attrezzatura presente' },
                ] as const).map(({ key, label }) => (
                  <label key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-medium transition-colors
                    ${form[key] ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    <input type="checkbox" className="accent-blue-600" checked={form[key] as boolean}
                      onChange={e => f(key, e.target.checked)} />
                    {label}
                  </label>
                ))}
              </div>
            </>
          )}

          {/* STEP 2 — Pernottamento e Costi */}
          {step === 2 && (
            <>
              {/* Toggle pernottamento — mutuamente esclusivo con forfettaria */}
              <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors
                ${form.pernottamento_necessario ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
                onClick={() => { f('pernottamento_necessario', !form.pernottamento_necessario); f('trasferta_forfettaria', false) }}>
                <input type="checkbox" className="accent-blue-600 w-4 h-4" checked={form.pernottamento_necessario}
                  onChange={() => {}} />
                <div>
                  <div className="font-semibold text-sm">Pernottamento necessario</div>
                  <div className="text-xs text-slate-500">La trasferta prevede uno o più pernottamenti con struttura ricettiva</div>
                </div>
              </label>

              {/* Toggle trasferta forfettaria — mutuamente esclusivo con pernottamento */}
              <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors
                ${form.trasferta_forfettaria ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}
                onClick={() => { f('trasferta_forfettaria', !form.trasferta_forfettaria); f('pernottamento_necessario', false); f('mezza_pensione', false) }}>
                <input type="checkbox" className="accent-amber-500 w-4 h-4" checked={form.trasferta_forfettaria}
                  onChange={() => {}} />
                <div>
                  <div className="font-semibold text-sm text-amber-800">Trasferta forfettaria completa al tecnico</div>
                  <div className="text-xs text-slate-500">Il tecnico gestisce direttamente l'alloggio — rimborso forfettario struttura per notte</div>
                </div>
              </label>

              {/* Campi pernottamento hotel */}
              {form.pernottamento_necessario && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border border-blue-100 rounded-xl p-4 bg-blue-50/30">
                  <div className="col-span-2">
                    <label className="label">Nome struttura</label>
                    <AutocompleteInput
                      campo="nome_albergo"
                      value={form.nome_albergo}
                      onChange={v => f('nome_albergo', v)}
                      onMetaSelect={meta => {
                        if (meta.ragione_sociale) f('ragione_sociale_albergo', meta.ragione_sociale)
                        if (meta.iban) f('iban_albergo', meta.iban)
                        if (meta.email) f('email_albergo', meta.email)
                        if (meta.telefono) f('telefono_albergo', meta.telefono)
                        if (meta.via) f('via_albergo', meta.via)
                        if (meta.citta) f('citta_albergo', meta.citta)
                        if (meta.nome_riferimento) f('nome_riferimento_albergo', meta.nome_riferimento)
                        if (meta.importo) f('importo_albergo', meta.importo)
                      }}
                      placeholder="es. Hotel Centrale"
                    />
                  </div>
                  <div>
                    <label className="label">Ragione sociale</label>
                    <AutocompleteInput
                      campo="ragione_sociale_albergo"
                      value={form.ragione_sociale_albergo}
                      onChange={v => f('ragione_sociale_albergo', v)}
                      onMetaSelect={meta => {
                        if (meta.iban) f('iban_albergo', meta.iban)
                        if (meta.email) f('email_albergo', meta.email)
                        if (meta.nome_albergo && !form.nome_albergo) f('nome_albergo', meta.nome_albergo)
                        if (meta.telefono) f('telefono_albergo', meta.telefono)
                        if (meta.via) f('via_albergo', meta.via)
                        if (meta.citta) f('citta_albergo', meta.citta)
                        if (meta.nome_riferimento) f('nome_riferimento_albergo', meta.nome_riferimento)
                        if (meta.importo) f('importo_albergo', meta.importo)
                      }}
                    />
                  </div>
                  <div>
                    <label className="label">IBAN (bonifico)</label>
                    <input className={`input font-mono text-sm ${form.iban_albergo && !ibanOk ? 'border-red-400 bg-red-50' : ''}`}
                      value={form.iban_albergo} onChange={e => f('iban_albergo', e.target.value.toUpperCase())}
                      placeholder="IT00A0000000000000000000000" />
                    {form.iban_albergo && !ibanOk && <p className="text-xs text-red-600 mt-1">Formato IBAN non valido</p>}
                  </div>
                  <div>
                    <label className="label">Importo albergo (€)</label>
                    <input type="number" step="0.01" min="0" className="input" value={form.importo_albergo}
                      onChange={e => f('importo_albergo', e.target.value)} placeholder="0.00" />
                  </div>
                  <div>
                    <label className="label">Email struttura</label>
                    <input type="email" className="input" value={form.email_albergo} onChange={e => f('email_albergo', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Telefono struttura</label>
                    <input type="tel" className="input" value={form.telefono_albergo} onChange={e => f('telefono_albergo', e.target.value)} placeholder="es. 049 1234567" />
                  </div>
                  <div>
                    <label className="label">Nome riferimento</label>
                    <input className="input" value={form.nome_riferimento_albergo} onChange={e => f('nome_riferimento_albergo', e.target.value)} placeholder="es. Mario Rossi" />
                  </div>
                  <div>
                    <label className="label">Via / Indirizzo</label>
                    <input className="input" value={form.via_albergo} onChange={e => f('via_albergo', e.target.value)} placeholder="es. Via Roma 12" />
                  </div>
                  <div>
                    <label className="label">Città</label>
                    <input className="input" value={form.citta_albergo} onChange={e => f('citta_albergo', e.target.value)} placeholder="es. Padova" />
                  </div>
                </div>
              )}

              {/* Campi trasferta forfettaria */}
              {form.trasferta_forfettaria && (
                <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/40 space-y-3">
                  <div>
                    <label className="label">Importo struttura per notte (€) *</label>
                    <input type="number" step="0.01" min="0" className="input"
                      value={form.importo_forfettario as string}
                      onChange={e => {
                        const v = parseFloat(e.target.value)
                        f('importo_forfettario', isNaN(v) ? '' : v)
                      }}
                      placeholder="es. 75.00" />
                  </div>
                  <p className="text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2">
                    La diaria viene calcolata sempre al valore base (senza mezza pensione).
                    Il rimborso struttura è {notti > 0 ? `€${importo_forf_parsed.toFixed(2)} × ${notti} notti = ${fmt_euro(tot_alb)}` : 'importo × n° notti'}.
                  </p>
                </div>
              )}

              {/* Sezione diaria */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-700">Schema diaria</p>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer text-sm font-medium transition-colors
                    ${form.nord_italia ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    <input type="checkbox" className="accent-blue-600" checked={form.nord_italia}
                      onChange={e => f('nord_italia', e.target.checked)} />
                    <div>
                      <div>Nord Italia</div>
                      <div className="text-xs opacity-70 font-normal">Piemonte, Liguria, Lombardia, Veneto, Emilia-Romagna, Friuli, Trentino</div>
                    </div>
                  </label>
                  {!form.trasferta_forfettaria && (
                    <label className={`flex-1 flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer text-sm font-medium transition-colors
                      ${form.mezza_pensione ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                      <input type="checkbox" className="accent-indigo-600" checked={form.mezza_pensione}
                        onChange={e => f('mezza_pensione', e.target.checked)} />
                      <div>
                        <div>Mezza pensione</div>
                        <div className="text-xs opacity-70 font-normal">La struttura fornisce anche la cena</div>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Detrazione pasti */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-700">Detrazione pasti</p>
                {form.mezza_pensione && (
                  <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                    Mezza pensione attiva: le cene sono incluse nel pernottamento.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Pranzi da togliere</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" min="0" step="1" className="input w-24 text-center"
                        value={form.pranzi_da_togliere}
                        onChange={e => f('pranzi_da_togliere', Math.max(0, parseInt(e.target.value) || 0))}
                      />
                      {(form.pranzi_da_togliere > 0) && (
                        <span className="text-sm text-red-600 font-medium">
                          − {fmt_euro(detrazione_pranzi)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{fmt_euro(importo_pranzo)}/pranzo × {numOp} op</p>
                  </div>
                  <div>
                    <label className="label">Cene da togliere</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" min="0" step="1" className="input w-24 text-center"
                        value={form.cene_da_togliere}
                        onChange={e => f('cene_da_togliere', Math.max(0, parseInt(e.target.value) || 0))}
                      />
                      {(form.cene_da_togliere > 0) && (
                        <span className="text-sm text-red-600 font-medium">
                          − {fmt_euro(detrazione_cene)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{fmt_euro(importo_cena)}/cena × {numOp} op</p>
                  </div>
                </div>
                {detrazione_pasti > 0 && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                    <span className="text-red-600">Totale detrazione pasti:</span>
                    <span className="font-bold text-red-700">− {fmt_euro(detrazione_pasti)}</span>
                  </div>
                )}
              </div>

              {/* Riquadro calcolo live */}
              {giorni > 0 && (
                <div className="rounded-xl border-2 border-green-200 bg-green-50 p-4 space-y-2">
                  <p className="text-sm font-bold text-green-800">📋 Riepilogo costi</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Zona:</span><span className="font-medium">{form.nord_italia ? 'Nord Italia' : 'Centro/Sud'}</span></div>
                    {!form.trasferta_forfettaria && <div className="flex justify-between"><span className="text-slate-600">Mezza pensione:</span><span className="font-medium">{form.mezza_pensione ? 'Sì' : 'No'}</span></div>}
                    {form.trasferta_forfettaria && <div className="flex justify-between"><span className="text-slate-600">Modalità:</span><span className="font-medium text-amber-700">Forfettaria (diaria base)</span></div>}
                    <div className="flex justify-between"><span className="text-slate-600">Diaria/op/giorno:</span><span className="font-bold text-blue-700">{fmt_euro(diaria)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Operatori:</span><span className="font-medium">× {numOp}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Giorni:</span><span className="font-medium">× {giorni}</span></div>
                    <div className="border-t border-green-200 pt-1.5 flex justify-between"><span className="text-slate-600">Totale diaria:</span><span className="font-bold">{fmt_euro(tot_op)}</span></div>
                    {form.trasferta_forfettaria && importo_forf_parsed > 0 && (
                      <div className="flex justify-between"><span className="text-slate-600">Rimborso struttura ({fmt_euro(importo_forf_parsed)} × {giorni} notti):</span><span className="font-bold">{fmt_euro(tot_alb)}</span></div>
                    )}
                    {form.pernottamento_necessario && tot_alb > 0 && (
                      <div className="flex justify-between"><span className="text-slate-600">Costo albergo:</span><span className="font-bold">{fmt_euro(tot_alb)}</span></div>
                    )}
                    {detrazione_pasti > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Detrazione pasti ({(form.pranzi_da_togliere > 0 ? `${form.pranzi_da_togliere} pranzi` : '')}
                          {(form.pranzi_da_togliere > 0 && form.cene_da_togliere > 0) ? ', ' : ''}
                          {(form.cene_da_togliere > 0 ? `${form.cene_da_togliere} cene` : '')}):</span>
                        <span className="font-bold">− {fmt_euro(detrazione_pasti)}</span>
                      </div>
                    )}
                    <div className="border-t-2 border-green-400 pt-1.5 flex justify-between items-center">
                      <span className="font-bold text-green-800">TOTALE TRASFERTA:</span>
                      <span className="text-xl font-bold text-green-700">{fmt_euro(tot)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="label">Beneficiario bonifico dipendenti *</label>
                {soloUnOp ? (
                  <input className="input bg-slate-100 text-slate-500 cursor-not-allowed"
                    value={form.nome_bonifico_dipendenti} readOnly />
                ) : (
                  <select className="select" value={form.nome_bonifico_dipendenti}
                    onChange={e => f('nome_bonifico_dipendenti', e.target.value)}>
                    <option value="">— Seleziona beneficiario —</option>
                    {operatoriCompilati.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                )}
              </div>
            </>
          )}

          {/* STEP 3 — Riepilogo */}
          {step === 3 && (
            <>
              <div className="space-y-4">
                <RiepilogoSezione titolo="Informazioni generali" righe={[
                  ['Progetto', form.progetto_cantiere],
                  ['Commessa', form.nr_commessa],
                  ['Project Manager', form.project_manager],
                  ['Destinazione', form.destinazione],
                  ['Motivo', form.motivo_trasferta],
                ]} />
                <RiepilogoSezione titolo="Date" righe={[
                  ['Partenza', fmt_data(form.data_partenza)],
                  ['Rientro', fmt_data(form.data_rientro)],
                  ['Giorni totali', String(giorni)],
                ]} />
                <RiepilogoSezione titolo="Personale" righe={[
                  ['Operatore 1', form.operatore_1],
                  ...(extraOp >= 1 && form.operatore_2 ? [['Operatore 2', form.operatore_2] as [string, string]] : []),
                  ...(extraOp >= 2 && form.operatore_3 ? [['Operatore 3', form.operatore_3] as [string, string]] : []),
                  ...(extraOp >= 3 && form.operatore_4 ? [['Operatore 4', form.operatore_4] as [string, string]] : []),
                  ...(form.mezzo_trasporto ? [['Mezzo', form.mezzo_trasporto] as [string, string]] : []),
                  ...(form.targa ? [['Targa', form.targa] as [string, string]] : []),
                ]} />
                <div className="rounded-xl border-2 border-green-200 bg-green-50 p-4">
                  <p className="text-sm font-bold text-green-800 mb-2">💰 Costi</p>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between"><span>Diaria ({fmt_euro(diaria)} × {numOp} op × {giorni} gg):</span><span className="font-medium">{fmt_euro(tot_op)}</span></div>
                    {form.trasferta_forfettaria && importo_forf_parsed > 0 && (
                      <div className="flex justify-between"><span>Forfait struttura ({fmt_euro(importo_forf_parsed)} × {giorni} notti):</span><span className="font-medium">{fmt_euro(tot_alb)}</span></div>
                    )}
                    {form.pernottamento_necessario && tot_alb > 0 && (
                      <div className="flex justify-between"><span>Albergo:</span><span className="font-medium">{fmt_euro(tot_alb)}</span></div>
                    )}
                    {detrazione_pasti > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Detrazione pasti ({form.pranzi_da_togliere > 0 ? `${form.pranzi_da_togliere} pranzi` : ''}{form.pranzi_da_togliere > 0 && form.cene_da_togliere > 0 ? ', ' : ''}{form.cene_da_togliere > 0 ? `${form.cene_da_togliere} cene` : ''}):</span>
                        <span className="font-medium">− {fmt_euro(detrazione_pasti)}</span>
                      </div>
                    )}
                    <div className="border-t border-green-300 pt-1 flex justify-between text-base font-bold text-green-800">
                      <span>TOTALE TRASFERTA:</span><span>{fmt_euro(tot)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="label">Note aggiuntive (opzionale)</label>
                <textarea className="input" rows={3} value={form.note} onChange={e => f('note', e.target.value)} placeholder="Informazioni aggiuntive per l'amministrazione..." />
              </div>

              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm space-y-1">
                <p className="font-semibold text-blue-800">📧 Email richiedente</p>
                <p className="text-blue-700 font-mono">{user?.email}</p>
                <p className="text-blue-600 text-xs">
                  Riceverai una notifica a questo indirizzo quando i bonifici vengono confermati dall'amministrazione.
                  Se l'email non è corretta, aggiornala nel tuo profilo utente prima di inviare.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <button onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
            className="btn-secondary flex items-center gap-1.5">
            <ChevronLeft size={15} />{step === 0 ? 'Annulla' : 'Indietro'}
          </button>

          <div className="flex items-center gap-2">
            {step === 3 && !isEdit && (
              <button onClick={() => bozzaMut.mutate()} disabled={bozzaMut.isPending || invioMut.isPending}
                className="btn-secondary flex items-center gap-1.5">
                {bozzaMut.isPending ? 'Salvataggio…' : '💾 Salva bozza'}
              </button>
            )}
            {step < 3 ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canNext}
                className="btn-primary flex items-center gap-1.5">
                Avanti <ChevronRight size={15} />
              </button>
            ) : isEdit ? (
              <button onClick={() => updateMut.mutate()} disabled={updateMut.isPending}
                className="btn-primary flex items-center gap-2 px-6">
                {updateMut.isPending ? 'Salvataggio…' : '💾 SALVA MODIFICHE'}
              </button>
            ) : (
              <button onClick={() => invioMut.mutate()} disabled={invioMut.isPending || bozzaMut.isPending}
                className="btn-primary flex items-center gap-2 px-6">
                {invioMut.isPending ? 'Invio in corso...' : '📤 INVIA RICHIESTA'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function RiepilogoSezione({ titolo, righe }: { titolo: string; righe: [string, string][] }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">{titolo}</div>
      <div className="divide-y divide-slate-100">
        {righe.map(([l, v]) => (
          <div key={l} className="flex px-3 py-2 text-sm">
            <span className="text-slate-500 w-36 shrink-0">{l}:</span>
            <span className="text-slate-800 font-medium">{v || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── DETTAGLIO TRASFERTA ──────────────────────────────────────────────────────

function DettaglioModal({ trasferta: trasfertaInit, onClose }: { trasferta: Trasferta; onClose: () => void }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico'].includes(user?.role ?? '')
  const isSuperadmin = user?.role === 'superadmin'
  const [editing, setEditing] = useState(false)
  const [trasferta, setTrasferta] = useState(trasfertaInit)
  const giorni = trasferta.giorni_totali || 0

  const [editCommessa, setEditCommessa] = useState(false)
  const [commessaVal, setCommessaVal] = useState(trasferta.nr_commessa)
  const { data: commesseList = [] } = useQuery<{ id: number; codice: string; ragione_sociale: string; label: string }[]>({
    queryKey: ['commesse-trasferta'],
    queryFn: () => configApi.getCommesse().then((r: any) => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const saveMut = useMutation({
    mutationFn: () => trasfertaApi.patchNrCommessa(trasferta.id, commessaVal),
    onSuccess: (r) => {
      setTrasferta(r.data)
      setCommessaVal(r.data.nr_commessa)
      setEditCommessa(false)
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      toast.success('Commessa aggiornata')
    },
    onError: () => toast.error('Errore aggiornamento'),
  })

  const inviaBozzaMut = useMutation({
    mutationFn: () => trasfertaApi.inviaBozza(trasferta.id),
    onSuccess: (r) => {
      setTrasferta(r.data)
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      toast.success('Trasferta inviata! Email di notifica inviata.')
    },
    onError: () => toast.error('Errore durante l\'invio'),
  })

  const canManageEscalation = ['admin', 'superadmin'].includes(user?.role ?? '')

  const sospendiMut = useMutation({
    mutationFn: ({ tipo, data }: { tipo: 'dipendenti' | 'albergo'; data: { fino_al?: string; indefinita: boolean; nota?: string } }) =>
      trasfertaApi.sospendiEscalation(trasferta.id, tipo, data),
    onSuccess: (r) => {
      setTrasferta(r.data)
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      toast.success('Escalation sospesa')
    },
    onError: () => toast.error('Errore nella sospensione dell\'escalation'),
  })

  const riprendiMut = useMutation({
    mutationFn: (tipo: 'dipendenti' | 'albergo') => trasfertaApi.riprendiEscalation(trasferta.id, tipo),
    onSuccess: (r) => {
      setTrasferta(r.data)
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      toast.success('Escalation riattivata')
    },
    onError: () => toast.error('Errore nella riattivazione dell\'escalation'),
  })

  const uploadDistintaAlbergoMut = useMutation({
    mutationFn: (file: File) => trasfertaApi.uploadDistintaAlbergo(trasferta.id, file),
    onSuccess: (r) => {
      setTrasferta(r.data)
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      toast.success('Distinta albergo caricata e bonifico confermato')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore nel caricamento della distinta'),
  })

  const uploadDistintaDipendentiMut = useMutation({
    mutationFn: ({ rigaId, file }: { rigaId: number; file: File }) =>
      trasfertaApi.uploadDistintaDipendenti(trasferta.id, rigaId, file),
    onSuccess: (r) => {
      setTrasferta(r.data)
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      toast.success('Distinta caricata e bonifico confermato')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore nel caricamento della distinta'),
  })

  const handleDownloadPdf = async () => {
    try {
      const res = await trasfertaApi.pdf(trasferta.id)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a'); a.href = url
      a.download = `trasferta_${trasferta.id}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore download PDF') }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="card w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h2 className="font-semibold text-slate-800">Trasferta #{trasferta.id}</h2>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-sm text-slate-500">{trasferta.progetto_cantiere} —</span>
              {editCommessa ? (
                <div className="flex items-center gap-1">
                  {commesseList.length > 0 ? (
                    <select className="select text-xs py-0.5 h-7" style={{ minWidth: 220 }}
                      value={commessaVal} onChange={e => setCommessaVal(e.target.value)}
                      autoFocus>
                      <option value="">— nessuna —</option>
                      {commesseList.map(c => <option key={c.id} value={c.codice}>{c.label}</option>)}
                    </select>
                  ) : (
                    <input className="input text-xs py-0.5 h-7" style={{ width: 160 }}
                      value={commessaVal} onChange={e => setCommessaVal(e.target.value)}
                      autoFocus onKeyDown={e => { if (e.key === 'Enter') saveMut.mutate(); if (e.key === 'Escape') setEditCommessa(false) }} />
                  )}
                  <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
                    className="text-green-600 hover:text-green-700 p-0.5">
                    <Check size={14} />
                  </button>
                  <button onClick={() => { setEditCommessa(false); setCommessaVal(trasferta.nr_commessa) }}
                    className="text-slate-400 hover:text-slate-600 p-0.5">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-sm text-slate-500">{trasferta.nr_commessa || '—'}</span>
                  {isAdmin && (
                    <button onClick={() => { setCommessaVal(trasferta.nr_commessa); setEditCommessa(true) }}
                      className="text-slate-300 hover:text-blue-500 p-0.5 transition-colors">
                      <Pencil size={12} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {trasferta.stato === 'bozza' && (
              <button
                onClick={() => inviaBozzaMut.mutate()}
                disabled={inviaBozzaMut.isPending}
                className="btn-primary flex items-center gap-1.5 text-sm"
              >
                <FileText size={14} /> {inviaBozzaMut.isPending ? 'Invio…' : 'Invia'}
              </button>
            )}
            <button onClick={handleDownloadPdf} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Download size={14} /> PDF
            </button>
            {isSuperadmin && (
              <button onClick={() => setEditing(true)} className="btn-secondary flex items-center gap-1.5 text-sm">
                <Pencil size={14} /> Modifica
              </button>
            )}
            <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Info generali */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {[
              ['Destinazione', trasferta.destinazione],
              ['Project Manager', trasferta.project_manager],
              ['Periodo', `${fmt_data(trasferta.data_partenza)} → ${fmt_data(trasferta.data_rientro)} (${giorni} gg)`],
              ['Richiedente', trasferta.richiedente_email],
            ].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-xs text-slate-400">{l}</div>
                <div className="font-medium text-slate-800">{v}</div>
              </div>
            ))}
          </div>

          {/* Operatori */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Operatori</p>
            <div className="flex flex-wrap gap-2">
              {[trasferta.operatore_1, trasferta.operatore_2, trasferta.operatore_3, trasferta.operatore_4]
                .filter(Boolean).map((op, i) => (
                  <span key={i} className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">{op}</span>
                ))}
            </div>
          </div>

          {/* Bonifici dipendenti (per operatore) */}
          {trasferta.bonifici_dipendenti && trasferta.bonifici_dipendenti.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Bonifici Dipendenti</p>
              <div className="space-y-1 text-sm">
                {trasferta.bonifici_dipendenti.map(r => (
                  <div key={r.id} className="flex justify-between items-center py-1.5 px-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-700">{r.intestatario_snapshot || '—'}</span>
                    <span className="text-slate-600">{fmt_euro(r.importo)}</span>
                    <span className={r.confermato_at ? 'text-green-700 font-medium' : 'text-yellow-700 font-medium'}>
                      {r.confermato_at ? '✅ Confermato' : '⏳ In attesa'}
                    </span>
                    {r.distinta_path && (
                      <DistintaRigaButton trasfertaId={trasferta.id} rigaId={r.id} />
                    )}
                    {!r.confermato_at && canManageEscalation && (
                      <UploadDistintaButton
                        pending={uploadDistintaDipendentiMut.isPending}
                        onFile={(file) => uploadDistintaDipendentiMut.mutate({ rigaId: r.id, file })}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Costi */}
          <div className="rounded-xl border-2 border-green-200 bg-green-50 p-4">
            <p className="text-sm font-bold text-green-800 mb-2">💰 Costi trasferta</p>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-600">Diaria/op/giorno:</span><span>{fmt_euro(trasferta.importo_giornaliero_operatore)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Totale diaria:</span><span>{fmt_euro((trasferta.totale_operatori || 0) - (trasferta.totale_detrazione_pasti || 0))}</span></div>
              {(trasferta.totale_detrazione_pasti || 0) > 0 && (
                <div className="flex justify-between text-xs text-slate-500"><span>di cui deduzioni pasti:</span><span>−{fmt_euro(trasferta.totale_detrazione_pasti)}</span></div>
              )}
              {trasferta.pernottamento_necessario && <div className="flex justify-between"><span className="text-slate-600">Albergo:</span><span>{fmt_euro(trasferta.importo_albergo)}</span></div>}
              <div className="border-t border-green-300 pt-1.5 flex justify-between text-base font-bold text-green-800">
                <span>TOTALE:</span><span>{fmt_euro(trasferta.totale_trasferta)}</span>
              </div>
            </div>
          </div>

          {/* Timeline bonifici */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Timeline Bonifici</p>
            <div className="space-y-2">
              <BonificoRow
                label="Bonifico dipendenti"
                beneficiario={trasferta.nome_bonifico_dipendenti}
                importo={(trasferta.totale_operatori || 0) - (trasferta.totale_detrazione_pasti || 0)}
                confermato_at={trasferta.bonifico_dipendenti_confermato_at}
                confermato_da={trasferta.bonifico_dipendenti_confermato_da}
                distinta_tipo={trasferta.distinta_dipendenti_path ? 'dipendenti' : undefined}
                trasferta_id={trasferta.id}
                sospensione={trasferta.escalation_sospensioni?.dipendenti}
                canManageEscalation={canManageEscalation}
                onSospendi={(data) => sospendiMut.mutate({ tipo: 'dipendenti', data })}
                onRiprendi={() => riprendiMut.mutate('dipendenti')}
                sospendiPending={sospendiMut.isPending}
                riprendiPending={riprendiMut.isPending}
              />
              {trasferta.pernottamento_necessario && (
                <BonificoRow
                  label="Bonifico albergo"
                  beneficiario={trasferta.ragione_sociale_albergo}
                  importo={trasferta.importo_albergo}
                  confermato_at={trasferta.bonifico_albergo_confermato_at}
                  confermato_da={trasferta.bonifico_albergo_confermato_da}
                  distinta_tipo={trasferta.distinta_albergo_path ? 'albergo' : undefined}
                  trasferta_id={trasferta.id}
                  sospensione={trasferta.escalation_sospensioni?.albergo}
                  canManageEscalation={canManageEscalation}
                  onSospendi={(data) => sospendiMut.mutate({ tipo: 'albergo', data })}
                  onRiprendi={() => riprendiMut.mutate('albergo')}
                  sospendiPending={sospendiMut.isPending}
                  riprendiPending={riprendiMut.isPending}
                  onUploadDistinta={(file) => uploadDistintaAlbergoMut.mutate(file)}
                  uploadPending={uploadDistintaAlbergoMut.isPending}
                />
              )}
            </div>
          </div>

          {trasferta.escalation_count > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm">
              <span className="font-semibold text-amber-700">⚠️ {trasferta.escalation_count} email di escalation inviate</span>
              {trasferta.ultima_escalation_at && (
                <span className="text-amber-600 ml-2">— ultima: {fmt_data(trasferta.ultima_escalation_at)}</span>
              )}
            </div>
          )}

          {trasferta.note && (
            <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm">
              <p className="font-medium text-slate-600 mb-1">Note</p>
              <p className="text-slate-700">{trasferta.note}</p>
            </div>
          )}
        </div>
      </div>
      {editing && (
        <NuovaTrasfertaModal
          trasfertaId={trasferta.id}
          initialData={trasferta}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setTrasferta(updated as Trasferta); setEditing(false) }}
        />
      )}
    </div>
  )
}

function SospendiPopover({ onSalva, onChiudi, pending }: {
  onSalva: (data: { fino_al?: string; indefinita: boolean; nota?: string }) => void
  onChiudi: () => void
  pending: boolean
}) {
  const [indefinita, setIndefinita] = useState(false)
  const [finoAl, setFinoAl] = useState('')
  const [nota, setNota] = useState('')

  const presetGiorni = (n: number) => {
    const d = new Date()
    d.setDate(d.getDate() + n)
    const pad = (x: number) => String(x).padStart(2, '0')
    setFinoAl(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`)
    setIndefinita(false)
  }

  const salva = () => {
    if (!indefinita && !finoAl) { toast.error('Scegli una data o sospendi indefinitamente'); return }
    onSalva({ fino_al: indefinita ? undefined : new Date(finoAl).toISOString(), indefinita, nota: nota || undefined })
  }

  return (
    <div className="absolute z-10 mt-1 w-64 card p-3 shadow-lg border border-slate-200 space-y-2 text-xs">
      <div className="flex gap-1.5">
        <button type="button" onClick={() => presetGiorni(1)} className="btn-secondary px-2 py-1 text-xs flex-1">+1g</button>
        <button type="button" onClick={() => presetGiorni(3)} className="btn-secondary px-2 py-1 text-xs flex-1">+3g</button>
        <button type="button" onClick={() => presetGiorni(7)} className="btn-secondary px-2 py-1 text-xs flex-1">+7g</button>
      </div>
      <input type="datetime-local" className="input text-xs py-1" value={finoAl}
        disabled={indefinita} onChange={e => setFinoAl(e.target.value)} />
      <label className="flex items-center gap-1.5 text-slate-600">
        <input type="checkbox" checked={indefinita} onChange={e => setIndefinita(e.target.checked)} />
        Sospendi indefinitamente
      </label>
      <textarea className="input text-xs py-1" rows={2} placeholder="Nota (opzionale)"
        value={nota} onChange={e => setNota(e.target.value)} />
      <div className="flex justify-end gap-1.5">
        <button type="button" onClick={onChiudi} className="btn-ghost px-2 py-1 text-xs">Annulla</button>
        <button type="button" onClick={salva} disabled={pending} className="btn-primary px-2 py-1 text-xs">
          {pending ? 'Salvo…' : 'Salva'}
        </button>
      </div>
    </div>
  )
}

function DistintaRigaButton({ trasfertaId, rigaId }: { trasfertaId: number; rigaId: number }) {
  const [downloading, setDownloading] = useState(false)
  const handleDownload = async () => {
    setDownloading(true)
    try {
      const res = await trasfertaApi.downloadDistinta(trasfertaId, 'dipendenti', rigaId)
      const contentDisposition = res.headers['content-disposition'] || ''
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      const filename = match ? match[1] : `distinta_dipendenti_${rigaId}.pdf`
      const contentType = res.headers['content-type']
      const url = URL.createObjectURL(new Blob([res.data], { type: typeof contentType === 'string' ? contentType : undefined }))
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Errore nel download della distinta')
    } finally {
      setDownloading(false)
    }
  }
  return (
    <button onClick={handleDownload} disabled={downloading}
      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50">
      <Download size={11} /> {downloading ? 'Download…' : 'Distinta'}
    </button>
  )
}

function UploadDistintaButton({ onFile, pending }: { onFile: (file: File) => void; pending?: boolean }) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  return (
    <>
      <input
        type="file" accept="application/pdf,image/*" className="hidden"
        ref={inputRef}
        onChange={e => { const file = e.target.files?.[0]; if (file) onFile(file); e.target.value = '' }}
      />
      <button
        type="button" onClick={() => inputRef.current?.click()} disabled={pending}
        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
      >
        <FileText size={11} /> {pending ? 'Caricamento…' : 'Carica distinta'}
      </button>
    </>
  )
}

function BonificoRow({
  label, beneficiario, importo, confermato_at, confermato_da, distinta_tipo, trasferta_id,
  sospensione, canManageEscalation, onSospendi, onRiprendi, sospendiPending, riprendiPending,
  onUploadDistinta, uploadPending,
}: {
  label: string; beneficiario?: string; importo?: number;
  confermato_at?: string; confermato_da?: string;
  distinta_tipo?: 'dipendenti' | 'albergo'; trasferta_id?: number
  sospensione?: SospensioneEscalation
  canManageEscalation?: boolean
  onSospendi?: (data: { fino_al?: string; indefinita: boolean; nota?: string }) => void
  onRiprendi?: () => void
  sospendiPending?: boolean
  riprendiPending?: boolean
  onUploadDistinta?: (file: File) => void
  uploadPending?: boolean
}) {
  const ok = !!confermato_at
  const [downloading, setDownloading] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const attiva = sospensioneAttiva(sospensione)

  const handleDownload = async () => {
    if (!trasferta_id || !distinta_tipo) return
    setDownloading(true)
    try {
      const res = await trasfertaApi.downloadDistinta(trasferta_id, distinta_tipo)
      const contentDisposition = res.headers['content-disposition'] || ''
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      const filename = match ? match[1] : `distinta_${distinta_tipo}.pdf`
      const contentType = res.headers['content-type']
      const url = URL.createObjectURL(new Blob([res.data], { type: typeof contentType === 'string' ? contentType : undefined }))
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Errore nel download della distinta')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className={`relative flex items-start gap-3 p-3 rounded-lg border ${ok ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className={`mt-0.5 shrink-0 ${ok ? 'text-green-600' : 'text-slate-400'}`}>
        {ok ? <CheckCircle2 size={16} /> : <Clock size={16} />}
      </div>
      <div className="flex-1 text-sm">
        <div className="font-medium text-slate-800">{label}</div>
        {beneficiario && <div className="text-slate-500 text-xs">{beneficiario} — {fmt_euro(importo)}</div>}
        {ok ? (
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-green-700 text-xs">Confermato il {fmt_data(confermato_at)}</span>
            {distinta_tipo && (
              <button onClick={handleDownload} disabled={downloading}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50">
                <Download size={11} /> {downloading ? 'Download…' : 'Distinta'}
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-slate-400 text-xs">In attesa di conferma</span>
            {canManageEscalation && onUploadDistinta && (
              <UploadDistintaButton onFile={onUploadDistinta} pending={uploadPending} />
            )}
            {canManageEscalation && attiva && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
                title={`Sospesa da ${sospensione?.sospesa_da} il ${fmt_data_ora(sospensione?.sospesa_at)}${sospensione?.nota ? ` — ${sospensione.nota}` : ''}`}>
                <PauseCircle size={11} />
                {sospensione?.indefinita ? 'Sospesa indefinitamente' : `Sospesa fino al ${fmt_data_ora(sospensione?.fino_al)}`}
              </span>
            )}
            {canManageEscalation && attiva && (
              <button onClick={onRiprendi} disabled={riprendiPending}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50">
                <PlayCircle size={11} /> {riprendiPending ? 'Riprendo…' : 'Riprendi ora'}
              </button>
            )}
            {canManageEscalation && !attiva && (
              <button onClick={() => setPopoverOpen(v => !v)}
                className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 underline">
                <PauseCircle size={11} /> Posticipa escalation
              </button>
            )}
          </div>
        )}
        {popoverOpen && (
          <SospendiPopover
            pending={!!sospendiPending}
            onChiudi={() => setPopoverOpen(false)}
            onSalva={(data) => { onSospendi?.(data); setPopoverOpen(false) }}
          />
        )}
      </div>
    </div>
  )
}

// ─── LISTA TRASFERTE ──────────────────────────────────────────────────────────

export default function Trasferte() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const isAdmin = ['admin', 'superadmin'].includes(user?.role ?? '')
  const isFatturaCortesiaAdmin = user?.email === FATTURA_CORTESIA_ADMIN_EMAIL
  const [tabFatturaCortesia, setTabFatturaCortesia] = useState(false)
  const [showNuova, setShowNuova] = useState(false)
  const [dettaglio, setDettaglio] = useState<Trasferta | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const [confirmBulk, setConfirmBulk] = useState(false)

  const { data: trasferte = [], isLoading } = useQuery({
    queryKey: ['trasferte'],
    queryFn: () => trasfertaApi.lista().then(r => r.data as Trasferta[]),
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (searchParams.get('confermato') === '1') {
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, qc, setSearchParams])

  const deleteMut = useMutation({
    mutationFn: (id: number) => trasfertaApi.elimina(id),
    onSuccess: () => {
      toast.success('Trasferta eliminata')
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      setConfirmDeleteId(null)
      setSelected(new Set())
    },
    onError: () => toast.error('Errore durante l\'eliminazione'),
  })

  const bulkDeleteMut = useMutation({
    mutationFn: (ids: number[]) => trasfertaApi.eliminaBulk(ids),
    onSuccess: (res) => {
      toast.success(`${res.data.eliminati} trasferte eliminate`)
      qc.invalidateQueries({ queryKey: ['trasferte'] })
      setSelected(new Set())
      setConfirmBulk(false)
    },
    onError: () => toast.error('Errore durante l\'eliminazione'),
  })

  const allIds = trasferte.map(t => t.id)
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds))
  const toggleOne = (id: number) => {
    const s = new Set(selected)
    s.has(id) ? s.delete(id) : s.add(id)
    setSelected(s)
  }

  // può eliminare la propria trasferta, admin elimina qualsiasi
  const puoEliminare = (t: Trasferta) =>
    isAdmin || t.richiedente_id === user?.id

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Car size={20} className="text-blue-600" />
          <h1 className="text-xl font-bold text-slate-800">Trasferte</h1>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && isAdmin && (
            <button
              onClick={() => setConfirmBulk(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
            >
              <Trash2 size={14} /> Elimina {selected.size} selezionate
            </button>
          )}
          <button onClick={() => setShowNuova(true)} className="btn-primary flex items-center gap-2">
            <Plus size={15} /> Nuova Richiesta
          </button>
        </div>
      </div>

      <FatturaCortesiaBanner />

      {(isFatturaCortesiaAdmin || isAdmin) && (
        <div className="flex items-center gap-2 border-b border-slate-200">
          <button
            onClick={() => setTabFatturaCortesia(false)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${!tabFatturaCortesia ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500'}`}
          >
            Trasferte
          </button>
          <button
            onClick={() => setTabFatturaCortesia(true)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tabFatturaCortesia ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500'}`}
          >
            Fatture di Cortesia
          </button>
        </div>
      )}

      {(isFatturaCortesiaAdmin || isAdmin) && tabFatturaCortesia ? (
        <PannelloFatturaCortesia />
      ) : isLoading ? (
        <div className="text-center py-16 text-slate-400">Caricamento...</div>
      ) : trasferte.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Car size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Nessuna trasferta registrata</p>
          <p className="text-sm mt-1">Clicca "Nuova Richiesta" per creare la prima</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
            <table className="w-full text-sm" style={{ minWidth: 640 }}>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {isAdmin && (
                    <th className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="rounded border-slate-300 text-blue-600"
                        title="Seleziona tutto"
                      />
                    </th>
                  )}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Data</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Progetto / Commessa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">Periodo</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">Gg</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">Operatori</th>
                  {isAdmin && <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden xl:table-cell">Richiedente</th>}
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Totale</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Stato</th>
                  <th className="px-4 py-3 w-28"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {trasferte.map(t => (
                  <tr
                    key={t.id}
                    className={`hover:bg-slate-50/50 transition-colors ${selected.has(t.id) ? 'bg-blue-50/40' : ''}`}
                  >
                    {isAdmin && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(t.id)}
                          onChange={() => toggleOne(t.id)}
                          className="rounded border-slate-300 text-blue-600"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt_data(t.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{t.progetto_cantiere}</div>
                      <div className="text-xs text-slate-400">#{t.nr_commessa}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600 hidden md:table-cell">
                      {fmt_data(t.data_partenza)} → {fmt_data(t.data_rientro)}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-blue-700 hidden sm:table-cell">{t.giorni_totali}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {[t.operatore_1, t.operatore_2, t.operatore_3, t.operatore_4].filter(Boolean).map((op, i) => (
                          <span key={i} className="bg-slate-100 text-slate-600 text-xs px-1.5 py-0.5 rounded">{op}</span>
                        ))}
                      </div>
                    </td>
                    {isAdmin && <td className="px-4 py-3 text-xs text-slate-500 hidden xl:table-cell">{t.richiedente_email}</td>}
                    <td className="px-4 py-3 text-right font-semibold text-green-700 whitespace-nowrap">{fmt_euro(t.totale_trasferta)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        <BadgeStato stato={t.stato}
                          bonifico_dip={t.bonifico_dipendenti_confermato_at}
                          bonifico_alb={t.bonifico_albergo_confermato_at}
                          pernottamento={t.pernottamento_necessario}
                          sospesa_dip={sospensioneAttiva(t.escalation_sospensioni?.dipendenti)}
                          sospesa_alb={sospensioneAttiva(t.escalation_sospensioni?.albergo)} />
                        {(() => {
                          const righe = t.bonifici_dipendenti ?? []
                          if (righe.length === 0) return null
                          const confermate = righe.filter(r => r.confermato_at).length
                          const tutte = confermate === righe.length
                          return (
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${tutte ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {confermate}/{righe.length} confermati
                            </span>
                          )
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setDettaglio(t)}
                          className="btn-ghost px-2 py-1 text-xs text-blue-600 flex items-center gap-1"
                          title="Dettaglio"
                        >
                          <Eye size={13} />
                          <span className="hidden sm:inline">Dettaglio</span>
                        </button>
                        {puoEliminare(t) && (
                          <button
                            onClick={() => setConfirmDeleteId(t.id)}
                            className="btn-ghost px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
                            title="Elimina"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showNuova && <NuovaTrasfertaModal onClose={() => setShowNuova(false)} />}
      {dettaglio && <DettaglioModal trasferta={dettaglio} onClose={() => setDettaglio(null)} />}

      {/* Dialog conferma delete singolo */}
      {confirmDeleteId !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="card w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Elimina trasferta</p>
                <p className="text-sm text-slate-500">Questa azione non può essere annullata.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDeleteId(null)} className="btn-secondary">Annulla</button>
              <button
                onClick={() => deleteMut.mutate(confirmDeleteId)}
                disabled={deleteMut.isPending}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                <Trash2 size={14} /> {deleteMut.isPending ? 'Eliminazione…' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog conferma bulk delete */}
      {confirmBulk && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="card w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Elimina {selected.size} trasferte</p>
                <p className="text-sm text-slate-500">Questa azione non può essere annullata.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmBulk(false)} className="btn-secondary">Annulla</button>
              <button
                onClick={() => bulkDeleteMut.mutate(Array.from(selected))}
                disabled={bulkDeleteMut.isPending}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                <Trash2 size={14} /> {bulkDeleteMut.isPending ? 'Eliminazione…' : `Elimina ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
